/*
 * Page widths, in one place (docs/ux-principles.md, "One frame everywhere").
 *
 *   wide      lists, grids and dashboards: fluid up to 1680px, so wide screens
 *             get more columns (Sites, Updates, Servers, Domains, Mail, Clients, the org
 *             Overview, Activity, the server pages, and the site page's frame).
 *   readable  settings, forms and detail pages with prose: up to 1100px. Settings
 *             is readable with its rail beside it, so its pages keep that measure.
 *
 * The shell wraps every page in PageLayout with the width pageWidth() gives
 * its address; a page doesn't set its own. Inside a wide page, a pane of long
 * text (the site's environment panes, through app/SectionFrame) keeps a
 * readable measure of its own.
 */
import type { ReactNode } from "react";
import { cx } from "../lib/cx";

export type PageWidth = "wide" | "readable" | "railed";

// railed: readable plus Settings' rail (13rem) and the gap beside it (2.5rem).
const MAX: Record<PageWidth, string> = { wide: "max-w-[1680px]", readable: "max-w-[1100px]", railed: "max-w-[1348px]" };

/** The longest a pane of prose gets inside a wide page. */
export const READABLE_PANE = "max-w-[1080px]";

export function PageLayout({ width, children, className }: { width: PageWidth; children: ReactNode; className?: string }) {
  return <div className={cx("mx-auto w-full px-4 pt-6 pb-16 sm:px-8 lg:pt-9", MAX[width], className)}>{children}</div>;
}

/**
 * Which width an address gets, from the part after /o/:org/. Lists, dashboards and the
 * site and server pages are wide; settings, billing and single detail pages are readable.
 */
export function pageWidth(rest: string): PageWidth {
  const [section = "", id] = rest.split("/").filter(Boolean);
  if (!section) return "wide"; // the org Overview
  if (["sites", "updates", "servers", "activity", "clients"].includes(section)) return "wide";
  if (section === "domains") return id ? "readable" : "wide";
  if (section === "mail") return !id ? "wide" : id === "mailboxes" ? "readable" : "wide";
  if (section === "settings") return "railed";
  return "readable"; // transfers, anything new until it's placed
}
